import { useState, useEffect, useRef } from 'react'
import { usePreferences } from '../contexts/PreferencesContext'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable,
} from '@dnd-kit/core'
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
} from '@dnd-kit/sortable'
import {
    useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
    Grid,
    Plus,
    Minus,
    Camera,
    X,
    Save,
    Eye,
    Settings,
    Palette,
    Monitor,
    Maximize2,
    Clock,
    Search
} from 'lucide-react'
import toast from 'react-hot-toast'

const DragDropScreenBuilder = ({ 
    screenId, 
    isOpen, 
    onClose, 
    availableCameras = [],
    onSave 
}) => {
    const { 
        preferences, 
        updateScreen, 
        getActiveScreen,
        setScreenDuration,
        getScreenDuration
    } = usePreferences()

    const [screenConfig, setScreenConfig] = useState(null)
    const [draggedCamera, setDraggedCamera] = useState(null)
    const [previewMode, setPreviewMode] = useState(false)
    const [unsavedChanges, setUnsavedChanges] = useState(false)
    const [cameraSearchTerm, setCameraSearchTerm] = useState('')

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Helper function to format location
    const formatLocation = (location) => {
        if (!location) return ''
        if (typeof location === 'string') return location
        
        const parts = [
            location.city,
            location.state
        ].filter(Boolean)
        
        return parts.join(', ')
    }

    // Filter cameras based on search term
    const filteredCameras = availableCameras.filter(camera => {
        if (!cameraSearchTerm) return true
        
        const searchLower = cameraSearchTerm.toLowerCase()
        return (
            camera.name?.toLowerCase().includes(searchLower) ||
            camera.description?.toLowerCase().includes(searchLower) ||
            camera.location?.city?.toLowerCase().includes(searchLower) ||
            camera.location?.state?.toLowerCase().includes(searchLower) ||
            camera.location?.exactLocation?.toLowerCase().includes(searchLower) ||
            camera.ipAddress?.toLowerCase().includes(searchLower)
        )
    })

    // Load screen configuration
    useEffect(() => {
        if (screenId && preferences.screens?.screenConfigs?.[screenId]) {
            setScreenConfig({ ...preferences.screens.screenConfigs[screenId] })
        } else {
            // Create new screen config
            setScreenConfig({
                id: screenId || 'new',
                name: 'New Screen',
                grid: {
                    type: 'symmetric',
                    columns: 2,
                    rows: 2,
                    cellGaps: 2,
                    aspectRatio: '16:9',
                    backgroundColor: '#1a1a1a'
                },
                cameras: [],
                displaySettings: {
                    showCameraNames: true,
                    showTimestamps: true,
                    autoHideControls: 3000
                }
            })
        }
    }, [screenId, preferences.screens?.screenConfigs])

    const updateConfig = (updates) => {
        setScreenConfig(prev => ({ ...prev, ...updates }))
        setUnsavedChanges(true)
    }

    const updateGridConfig = (gridUpdates) => {
        setScreenConfig(prev => ({
            ...prev,
            grid: { ...prev.grid, ...gridUpdates }
        }))
        setUnsavedChanges(true)
    }

    const handleSave = () => {
        if (!screenConfig) return

        if (screenId && screenId !== 'new') {
            updateScreen(screenId, screenConfig)
        } else {
            // Handle new screen creation via parent
            if (onSave) {
                onSave(screenConfig)
            }
        }
        
        setUnsavedChanges(false)
        toast.success('Screen saved successfully')
    }

    const handleDragStart = (event) => {
        const { active } = event
        
        // Check if dragging from sidebar (camera ID)
        let camera = availableCameras.find(c => c._id === active.id)
        
        // Check if dragging from grid (camera-cameraId-grid-cell-row-col)
        if (!camera && active.id.startsWith('camera-')) {
            const match = active.id.match(/camera-(.+?)-grid-cell-(\d+)-(\d+)/)
            if (match) {
                const cameraId = match[1]
                camera = availableCameras.find(c => c._id === cameraId)
            }
        }
        
        setDraggedCamera(camera)
    }

    const handleDragEnd = (event) => {
        const { active, over } = event
        setDraggedCamera(null)

        if (!over) return

        // Only handle sidebar camera drops (since we removed grid-to-grid dragging)
        const draggedCamera = availableCameras.find(c => c._id === active.id)
        if (!draggedCamera) return

        // Parse drop target position
        const dropMatch = over.id.match(/grid-cell-(\d+)-(\d+)/)
        if (!dropMatch) return

        const toRow = parseInt(dropMatch[1])
        const toCol = parseInt(dropMatch[2])

        // Get current cameras and remove any existing camera from this position
        let updatedCameras = screenConfig.cameras.filter(c => 
            !(c.position.row === toRow && c.position.col === toCol)
        )

        // Also remove this camera from any other position (in case it was already placed)
        updatedCameras = updatedCameras.filter(c => c.cameraId !== draggedCamera._id)

        // Add dragged camera to new position
        const newCamera = {
            cameraId: draggedCamera._id,
            position: { row: toRow, col: toCol, rowSpan: 1, colSpan: 1 },
            size: 'normal'
        }

        updateConfig({
            cameras: [...updatedCameras, newCamera]
        })
    }

    const removeCameraFromGrid = (cameraId) => {
        updateConfig({
            cameras: screenConfig.cameras.filter(c => c.cameraId !== cameraId)
        })
    }

    const adjustGridSize = (dimension, delta) => {
        const currentValue = screenConfig.grid[dimension]
        const newValue = Math.max(1, Math.min(8, currentValue + delta))
        
        // Remove cameras that would be outside the new grid
        const filteredCameras = screenConfig.cameras.filter(camera => {
            const { row, col } = camera.position
            return dimension === 'columns' 
                ? col <= newValue 
                : row <= newValue
        })

        updateGridConfig({ 
            [dimension]: newValue,
        })
        
        if (filteredCameras.length !== screenConfig.cameras.length) {
            updateConfig({ cameras: filteredCameras })
        }
    }

    const getCameraAtPosition = (row, col) => {
        return screenConfig?.cameras.find(c => 
            c.position.row === row && c.position.col === col
        )
    }

    const getCameraById = (cameraId) => {
        return availableCameras.find(c => c._id === cameraId)
    }

    if (!isOpen || !screenConfig) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            Screen Builder
                        </h2>
                        <input
                            type="text"
                            value={screenConfig.name}
                            onChange={(e) => updateConfig({ name: e.target.value })}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Screen name..."
                        />
                        {unsavedChanges && (
                            <span className="text-sm text-orange-600 dark:text-orange-400">
                                Unsaved changes
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPreviewMode(!previewMode)}
                            className={`px-3 py-2 rounded-lg font-medium ${
                                previewMode
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            <Eye className="w-4 h-4 inline mr-1" />
                            Preview
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!unsavedChanges}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save className="w-4 h-4 inline mr-1" />
                            Save
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="flex h-[calc(95vh-80px)]">
                        {/* Left Sidebar - Controls */}
                        {!previewMode && (
                            <div className="w-80 border-r dark:border-gray-700 overflow-y-auto">
                            <div className="p-4 space-y-6">
                                {/* Grid Settings */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <Grid className="w-4 h-4" />
                                        Grid Layout
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Columns</span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => adjustGridSize('columns', -1)}
                                                    className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                                <span className="w-8 text-center text-sm font-medium">
                                                    {screenConfig.grid.columns}
                                                </span>
                                                <button
                                                    onClick={() => adjustGridSize('columns', 1)}
                                                    className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Rows</span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => adjustGridSize('rows', -1)}
                                                    className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                                <span className="w-8 text-center text-sm font-medium">
                                                    {screenConfig.grid.rows}
                                                </span>
                                                <button
                                                    onClick={() => adjustGridSize('rows', 1)}
                                                    className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                Cell Gaps
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="10"
                                                value={screenConfig.grid.cellGaps}
                                                onChange={(e) => updateGridConfig({ cellGaps: parseInt(e.target.value) })}
                                                className="w-full"
                                            />
                                            <span className="text-xs text-gray-500">{screenConfig.grid.cellGaps}px</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Auto-Rotation Duration */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        Auto-Rotation
                                    </h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                Display Duration (seconds)
                                            </label>
                                            <input
                                                type="number"
                                                min="5"
                                                max="300"
                                                value={screenConfig ? getScreenDuration(screenConfig.id) : 30}
                                                onChange={(e) => {
                                                    if (screenConfig) {
                                                        setScreenDuration(screenConfig.id, parseInt(e.target.value))
                                                    }
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                placeholder="30"
                                            />
                                            <span className="text-xs text-gray-500">How long this screen shows in auto-rotation</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Display Settings */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <Settings className="w-4 h-4" />
                                        Display Settings
                                    </h3>
                                    <div className="space-y-3">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={screenConfig.displaySettings.showCameraNames}
                                                onChange={(e) => updateConfig({
                                                    displaySettings: {
                                                        ...screenConfig.displaySettings,
                                                        showCameraNames: e.target.checked
                                                    }
                                                })}
                                                className="rounded"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                Show camera names
                                            </span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={screenConfig.displaySettings.showTimestamps}
                                                onChange={(e) => updateConfig({
                                                    displaySettings: {
                                                        ...screenConfig.displaySettings,
                                                        showTimestamps: e.target.checked
                                                    }
                                                })}
                                                className="rounded"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                Show timestamps
                                            </span>
                                        </label>
                                        <div>
                                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                Background Color
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={screenConfig.grid.backgroundColor}
                                                    onChange={(e) => updateGridConfig({ backgroundColor: e.target.value })}
                                                    className="w-8 h-8 rounded border"
                                                />
                                                <input
                                                    type="text"
                                                    value={screenConfig.grid.backgroundColor}
                                                    onChange={(e) => updateGridConfig({ backgroundColor: e.target.value })}
                                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Available Cameras */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <Camera className="w-4 h-4" />
                                        Available Cameras ({filteredCameras.length})
                                        {cameraSearchTerm && (
                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                of {availableCameras.length}
                                            </span>
                                        )}
                                    </h3>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Search className="w-4 h-4 text-gray-500" />
                                        <input
                                            type="text"
                                            placeholder="Search cameras..."
                                            value={cameraSearchTerm}
                                            onChange={(e) => setCameraSearchTerm(e.target.value)}
                                            className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                        {cameraSearchTerm && (
                                            <button
                                                onClick={() => setCameraSearchTerm('')}
                                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                title="Clear search"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        <SortableContext items={filteredCameras.map(c => c._id)} strategy={rectSortingStrategy}>
                                            {filteredCameras.map(camera => (
                                                <DraggableCamera key={camera._id} camera={camera} formatLocation={formatLocation} />
                                            ))}
                                        </SortableContext>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Main Area - Grid Builder */}
                    <div className="flex-1 p-6 overflow-auto">
                        <div className="max-w-full max-h-full">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {previewMode ? 'Preview Mode' : 'Drag cameras into the grid below'}
                                </h3>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {screenConfig.grid.columns}×{screenConfig.grid.rows} • {screenConfig.cameras.length} cameras
                                </div>
                            </div>

                            <div 
                                className="grid gap-2 w-full max-w-6xl mx-auto"
                                style={{
                                    gridTemplateColumns: `repeat(${screenConfig.grid.columns}, 1fr)`,
                                    gridTemplateRows: `repeat(${screenConfig.grid.rows}, 1fr)`,
                                    gap: `${screenConfig.grid.cellGaps}px`,
                                    backgroundColor: screenConfig.grid.backgroundColor,
                                    padding: `${screenConfig.grid.cellGaps * 2}px`,
                                    borderRadius: '8px',
                                    minHeight: previewMode ? '70vh' : '60vh'
                                }}
                            >
                                {Array.from({ length: screenConfig.grid.rows }).map((_, rowIndex) =>
                                    Array.from({ length: screenConfig.grid.columns }).map((_, colIndex) => {
                                        const row = rowIndex + 1
                                        const col = colIndex + 1
                                        const camera = getCameraAtPosition(row, col)
                                        
                                        return (
                                            <GridCell
                                                key={`${row}-${col}`}
                                                row={row}
                                                col={col}
                                                camera={camera}
                                                cameraData={camera ? getCameraById(camera.cameraId) : null}
                                                onRemoveCamera={removeCameraFromGrid}
                                                showDetails={screenConfig.displaySettings.showCameraNames}
                                                isPreview={previewMode}
                                                formatLocation={formatLocation}
                                            />
                                        )
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                    
                {/* DragOverlay for dragging feedback */}
                <DragOverlay>
                    {draggedCamera && (
                        <div className="bg-white dark:bg-gray-700 border-2 border-blue-500 rounded-lg p-2 shadow-lg">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {draggedCamera.name}
                            </div>
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
            </div>
        </div>
    )
}

// Draggable Camera Component
const DraggableCamera = ({ camera, formatLocation }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: camera._id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="p-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
        >
            <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {camera.name}
                </span>
            </div>
            {camera.location && (
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {formatLocation(camera.location)}
                </div>
            )}
        </div>
    )
}

// Grid Cell Component
const GridCell = ({ 
    row, 
    col, 
    camera, 
    cameraData, 
    onRemoveCamera, 
    showDetails, 
    isPreview,
    formatLocation
}) => {
    const cellId = `grid-cell-${row}-${col}`
    
    // Use droppable for all cells
    const {
        isOver,
        setNodeRef
    } = useDroppable({
        id: cellId
    })

    return (
        <div
            ref={setNodeRef}
            className={`relative aspect-video border-2 border-dashed rounded-lg transition-all ${
                camera 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : isOver
                        ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
        >
            {camera && cameraData ? (
                <div className="relative w-full h-full">
                    {/* Camera Content */}
                    <div className="w-full h-full bg-gray-900 rounded-lg overflow-hidden">
                        {isPreview ? (
                            <div className="w-full h-full flex items-center justify-center text-white">
                                <div className="text-center">
                                    <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <div className="text-sm font-medium">{cameraData.name}</div>
                                    {cameraData.location && (
                                        <div className="text-xs opacity-70">
                                            {formatLocation(cameraData.location)}
                                        </div>
                                    )}
                                    <div className="text-xs opacity-50">Live Stream</div>
                                </div>
                            </div>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                                <div className="text-center">
                                    <Camera className="w-6 h-6 mx-auto mb-1 opacity-70" />
                                    <div className="text-xs font-medium">{cameraData.name}</div>
                                    {cameraData.location && (
                                        <div className="text-xs opacity-50">
                                            {formatLocation(cameraData.location)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>


                    {/* Remove Button */}
                    {!isPreview && (
                        <button
                            onClick={() => onRemoveCamera(camera.cameraId)}
                            className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>
            ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                    <div className="text-center">
                        <Plus className="w-6 h-6 mx-auto mb-1 opacity-50" />
                        <div className="text-xs">Drop camera here</div>
                        <div className="text-xs opacity-70">{row},{col}</div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DragDropScreenBuilder